import { invoke } from '@tauri-apps/api/core'
import maplibregl from 'maplibre-gl'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { getActiveOfflineOwnerUserId } from '@/services/offlineAuthBridge'

// URL grammar: rcoffline://<styleId>/<sourceId>/<z>/<x>/<y>.<ext>
const PROTOCOL = 'rcoffline'
const DEBUG_FLAG_KEY = 'routecampDebugOfflineTiles'

function buildMissingTileResponse(ext: string): { data: Uint8Array | null } {
  const normalized = ext.toLowerCase()
  if (normalized === 'pbf' || normalized === 'mvt') {
    return { data: new Uint8Array() }
  }

  return { data: null }
}

function shouldLogOfflineTileMisses(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return [window.location.search, window.location.hash].some((value) =>
    value.includes(`${DEBUG_FLAG_KEY}=1`),
  )
}

export function registerOfflineProtocol(): void {
  maplibregl.addProtocol(PROTOCOL, async (params) => {
    const url = params.url.replace(`${PROTOCOL}://`, '')
    const parts = url.split('/')
    if (parts.length !== 5) {
      throw new Error(`Invalid rcoffline URL: ${params.url}`)
    }

    const [encodedStyleId, encodedSourceId, zStr, xStr, yWithExt] = parts
    let styleId: string
    let sourceId: string
    try {
      styleId = decodeURIComponent(encodedStyleId)
      sourceId = decodeURIComponent(encodedSourceId)
    } catch {
      throw new Error(`Invalid rcoffline URL: ${params.url}`)
    }
    const dotIdx = yWithExt.lastIndexOf('.')
    const y = parseInt(yWithExt.substring(0, dotIdx), 10)
    const ext = yWithExt.substring(dotIdx + 1)
    const z = parseInt(zStr, 10)
    const x = parseInt(xStr, 10)

    try {
      const expectedOwnerUserId = getActiveOfflineOwnerUserId()
      if (expectedOwnerUserId === null) throw new Error('Offline data is locked')
      const result = await invoke<{ bytes: number[]; content_type: string }>('read_offline_tile', {
        styleId,
        sourceId,
        z,
        x,
        y,
        ext,
      })
      if (getActiveOfflineOwnerUserId() !== expectedOwnerUserId) {
        throw new Error('Offline account changed while reading a tile')
      }
      const data = new Uint8Array(result.bytes).buffer
      return { data }
    } catch (error) {
      if (shouldLogOfflineTileMisses()) {
        logService.debug(LOG_CATEGORIES.OFFLINE, 'Offline tile not available in cache', {
          url: params.url,
          styleId,
          sourceId,
          z,
          x,
          y,
          ext,
          error,
        })
      }
      // Tile not in offline cache — signal absence so MapLibre can decide fallback
      return buildMissingTileResponse(ext)
    }
  })

  logService.info(LOG_CATEGORIES.OFFLINE, 'Registered rcoffline:// MapLibre protocol')
}

type StyleValue = maplibregl.StyleSpecification | string

type RewritableStyleSource = {
  type?: string
  tiles?: string[]
  url?: string
  attribution?: string
  minzoom?: number
  maxzoom?: number
}

async function resolveStyleJson(style: StyleValue): Promise<maplibregl.StyleSpecification | null> {
  if (typeof style === 'string') {
    try {
      const resp = await fetch(style)
      if (!resp.ok) return null
      return (await resp.json()) as maplibregl.StyleSpecification
    } catch (error) {
      logService.warn(LOG_CATEGORIES.OFFLINE, 'Failed to resolve style JSON for offline rewrite', {
        style,
        error,
      })
      return null
    }
  }
  return JSON.parse(JSON.stringify(style)) as maplibregl.StyleSpecification
}

function normalizeTileExtension(ext: string): string {
  const normalized = ext.toLowerCase()
  if (normalized === 'jpeg') return 'jpg'
  if (normalized === 'mvt') return 'pbf'
  return normalized
}

function inferOfflineExtension(source: RewritableStyleSource): string {
  const tileTemplate = source.tiles?.[0]
  const templateMatch = tileTemplate?.match(/\.(pbf|mvt|png|jpg|jpeg|webp|avif)(\?|$)/i)
  if (templateMatch) return normalizeTileExtension(templateMatch[1])

  return source.type === 'vector' ? 'pbf' : 'png'
}

export async function applyOfflineRewrite(
  style: StyleValue,
  workspaceId: string,
  styleId: string,
  downloadedSourceIds: string[],
  sourceExtensions: Record<string, string> = {},
  sourceMinzooms: Record<string, number> = {},
  sourceMaxzooms: Record<string, number> = {},
): Promise<StyleValue> {
  if (downloadedSourceIds.length === 0) return style

  const json = await resolveStyleJson(style)
  if (!json) return style

  const sources = json.sources as Record<string, RewritableStyleSource>

  for (const sourceId of downloadedSourceIds) {
    if (!sources[sourceId]) continue

    const src = sources[sourceId]
    const ext = normalizeTileExtension(sourceExtensions[sourceId] ?? inferOfflineExtension(src))
    const minzoom = sourceMinzooms[sourceId]
    const maxzoom = sourceMaxzooms[sourceId]

    const rewrittenSource = {
      ...src,
      tiles: [
        `${PROTOCOL}://${encodeURIComponent(styleId)}/${encodeURIComponent(sourceId)}/{z}/{x}/{y}.${ext}`,
      ],
      ...(typeof minzoom === 'number' ? { minzoom } : {}),
      ...(typeof maxzoom === 'number' ? { maxzoom } : {}),
    }
    delete rewrittenSource.url
    sources[sourceId] = rewrittenSource
  }

  return json
}
